import { styled } from '@material-ui/core';

import { down } from '../../../../assets/style/breakpointHelpers';
import { domTag } from '../../../utils/styledFilter';

// The trusted-invite notice is rendered in two places on step 1: inside the
// party-branded hero (on-brand — inherits the hero's computed on-brand text
// colour and sits on a translucent white pill so it reads on any party colour)
// and above the plain PageHeaderBlock when no customization is published
// (on-page — brand tokens on the page surface, dark-mode aware via the vars).

export type TrustedInviteNoticeVariant = 'on-brand' | 'on-page';

const SURFACE: Record<TrustedInviteNoticeVariant, Record<string, unknown>> = {
	'on-brand': {
		'color': 'inherit',
		'background': 'rgba(255, 255, 255, 0.14)',
		'border': '1px solid rgba(255, 255, 255, 0.28)',
		'& .notice-eyebrow': { opacity: 0.85 },
		'& .notice-text': { opacity: 0.94 },
	},
	'on-page': {
		'color': 'var(--color-text-primary)',
		'background': 'color-mix(in srgb, var(--color-brand-primary) 9%, var(--color-bg-elevated))',
		'border': '1px solid color-mix(in srgb, var(--color-brand-primary) 26%, transparent)',
		'& .notice-eyebrow': { color: 'var(--color-brand-primary)' },
		'& .notice-glyph': { color: 'var(--color-brand-primary)' },
		'& .notice-text': { color: 'var(--color-text-secondary)' },
	},
};

export const NoticeRoot = styled(domTag('div', ['variant']))(
	({ variant = 'on-page' }: { variant?: TrustedInviteNoticeVariant }) => ({
		display: 'flex',
		alignItems: 'flex-start',
		gap: 12,
		alignSelf: 'flex-start',
		maxWidth: '64ch',
		boxSizing: 'border-box',
		padding: '12px 16px 12px 14px',
		borderRadius: 'var(--radius-default, 4px)',
		...SURFACE[variant],
		[down('sm')]: {
			gap: 10,
			padding: '10px 12px',
			maxWidth: '100%',
		},
	})
);

export const NoticeGlyph = styled('span')({
	'flex': 'none',
	'display': 'inline-flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'width': 28,
	'height': 28,
	'marginTop': 1,
	'color': 'inherit',
	'& svg': {
		width: 22,
		height: 22,
	},
	[down('sm')]: {
		'width': 24,
		'height': 24,
		'& svg': { width: 20, height: 20 },
	},
});

export const NoticeBody = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
	minWidth: 0,
});

export const NoticeEyebrow = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-medium)',
	fontWeight: 600,
	fontSize: 11,
	lineHeight: 1.3,
	letterSpacing: 'var(--tracking-widest)',
	textTransform: 'uppercase',
	fontVariantNumeric: 'tabular-nums',
});

export const NoticeText = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-sm-size)',
	lineHeight: 1.5,
	[down('sm')]: {
		fontSize: 'var(--text-caption-size)',
	},
});
